//@ts-strict-ignore
import React, { memo } from 'react';
import styled from 'styled-components';
import isFunction from '../../react-data-grid/utils/isFunction';
import { formatValue } from './FormatValue';
import { IFormatterProps } from '../../../@types/workspaceTypes';

interface INumberCellProps {
  align?: 'left' | 'right';
}

const NumberCell = styled.div<INumberCellProps>`
  text-align: ${(props) => (props.align === 'left' ? 'left' : 'right')};
`;

const PrimaryNumberCell = styled.div<INumberCellProps>`
  text-align: ${(props) => (props.align === 'left' ? 'left' : 'right')};
  font-weight: bold;
  font-size: 16px;
`;

const removeTrailingZeros = (numeric) => {
  let str = numeric.toString();
  while (str.endsWith('0')) {
    str = str.slice(0, -1);
  }
  return parseFloat(str);
};

export type INumericFormatterProps = {
  column: IFormatterProps['column'];
  dependentValues: {
    options: {
      title?: any;
      format?: {
        decimals: Function | number;
        suffix: string;
        prefix: string | ((rowData: any) => string);
        thousandSeparator: any;
      };
      suppressTrailingZeros?: boolean;
      suppressZeroes?: boolean;
      infinityLimit?: number;
      primaryNumber: boolean;
    };
    rowData: any;
  };
  type: string;
  value: any;
} & INumberCellProps;

const NumericFormatter = ({ align, dependentValues, type, value }: INumericFormatterProps) => {
  if (value === false || value === null || value === undefined) return <NumberCell />;
  const suppressZeroes = dependentValues.options?.suppressZeroes;
  if (suppressZeroes && value === 0) return <NumberCell />;

  const format = dependentValues.options?.format;
  let decimals = type === 'integer' ? 0 : 2;

  if (format && (format.decimals || format.decimals === 0)) {
    if (typeof format.decimals === 'function') {
      decimals = format.decimals(dependentValues.rowData);
    } else {
      decimals = format.decimals;
    }
  }

  const suffix = format ? format.suffix : '';
  let prefix = '';
  if (format?.prefix) {
    if (typeof format.prefix === 'function') {
      prefix = format.prefix(dependentValues.rowData);
    } else {
      prefix = format.prefix;
    }
  }
  const formattedValue: string | number = formatValue(value, { dependentValues, decimals });

  let title = dependentValues.options?.title;
  if (isFunction(title)) {
    title = title(dependentValues.rowData);
  }
  if (!title) {
    title = formattedValue;
  }

  const Cell = dependentValues.options?.primaryNumber ? PrimaryNumberCell : NumberCell;
  return (
    <Cell title={title} align={align}>
      {prefix}
      {formattedValue}
      {suffix}
    </Cell>
  );
};
NumericFormatter.defaultProps = {
  align: 'right',
  type: 'numeric',
};

export default memo(NumericFormatter, (prevProps, nextProps) => prevProps.value === nextProps.value);
